import React, { useCallback, useEffect, useRef, useState } from 'react';
import { 
  ArrowLeft, 
  MoreVertical, 
  Users,
  Loader2,
  Check,
  Clock,
  Calendar
} from 'lucide-react';
import { Chat, Message } from '../../types';
import { useChatStore } from '../../stores/chatStore';
import { useAuthStore } from '../../stores/authStore';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import { FastReply } from './FastReply';
import { DropdownMenu } from '../common/DropdownMenu';
import { QuickActionPopup } from './QuickActionPopup';
import { AddBookingModal } from './AddBookingModal';

interface ChatWindowProps {
  chat: Chat;
  onBack?: () => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ chat, onBack }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [showFastReply, setShowFastReply] = useState(false);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [insertMessage, setInsertMessage] = useState<string>('');
  const [clearOnClose, setClearOnClose] = useState(false);
  const [quickActionSearchQuery, setQuickActionSearchQuery] = useState<string>('');
  
  const { 
    messages, 
    loadMessages, 
    sendMessage,
    isLoading, 
    activeChat,
    isMessagePolling,
    addMessage,
    updateChatStatus
  } = useChatStore();
  const { user } = useAuthStore();

  const chatMessages = messages[chat.userId] || [];

  // Handle Android back button to close popups
  useEffect(() => {
    const handleAndroidBack = () => {
      // Close popups in order of priority
      if (showAddBooking) {
        setShowAddBooking(false);
        return true; // Prevent default back behavior
      }
      if (showQuickAction) {
        setShowQuickAction(false);
        return true; // Prevent default back behavior
      }
      if (showFastReply) {
        setShowFastReply(false);
        return true; // Prevent default back behavior
      }
      if (showDropdownMenu) {
        setShowDropdownMenu(false);
        return true; // Prevent default back behavior
      }
      if (showChatInfo) {
        setShowChatInfo(false);
        return true; // Prevent default back behavior
      }
      return false; // Allow default back behavior
    };

    // Add event listener for Android back button
    const handleBackButton = (e: Event) => {
      if (handleAndroidBack()) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Listen for the Android back button event
    document.addEventListener('backbutton', handleBackButton, false);
    
    // Also handle browser back button for mobile web
    const handlePopState = () => {
      if (showQuickAction || showFastReply || showDropdownMenu || showChatInfo) {
        handleAndroidBack();
        // Push a new state to prevent actual navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to handle back button
    window.history.pushState(null, '', window.location.href);

    return () => {
      document.removeEventListener('backbutton', handleBackButton, false);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showAddBooking, showQuickAction, showFastReply, showDropdownMenu, showChatInfo]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages.length]);

  // Load messages when chat changes
  useEffect(() => {
    if (chat.userId) {
      console.log('Loading messages for chat:', chat.userId);
      loadMessages(chat.userId);
    }
  }, [chat.userId, loadMessages]);

  // Mark messages as read when chat is active (placeholder for future implementation)
  useEffect(() => {
    if (activeChat?.userId === chat.userId && chatMessages.length > 0) {
      // TODO: Implement mark messages as read functionality
      console.log('Chat is active, messages could be marked as read');
    }
  }, [activeChat?.userId, chat.userId, chatMessages]);

  // Handle scroll for pagination
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    
    if (scrollTop === 0 && !isLoadingMore && chatMessages.length > 0) {
      setIsLoadingMore(true);
      loadMessages(chat.userId).finally(() => {
        setIsLoadingMore(false);
      });
    }
  };

  const getChatTitle = () => {
    return chat.name;
  };

  const getChatSubtitle = () => {
    return chat.tripName;
  };

  const handleSendMessage = (content: string) => {
    if (!content.trim() || !user) return;
    
    sendMessage(chat.userId, {
      message: content,
      type: 'Text'
    });
  };

  const handleReplyMessage = useCallback((msg: Message) => {
    // TODO: Implement reply functionality
    console.log('Reply to:', msg);
  }, []);

  const handleEditMessage = useCallback((msg: Message) => {
    // TODO: Implement edit functionality
    console.log('Edit:', msg);
  }, []);

  const handleDeleteMessage = useCallback((msg: Message) => {
    // TODO: Implement delete functionality
    console.log('Delete:', msg);
  }, []);

  const handleRetryMessage = useCallback((message: Message) => {
    if (!message.sendMessage?.data?.message) return;

    sendMessage(chat.userId, {
      message: message.sendMessage.data.message,
      type: message.type
    });
  }, [chat.userId, sendMessage]);

  const handleMarkPending = () => {
    updateChatStatus(chat.userId, 'pending');
  };

  const handleMarkRead = () => {
    updateChatStatus(chat.userId, 'read');
  };

  const handleQuickActionSelect = (message: string) => {
    // Insert the message into the input field instead of sending directly
    setInsertMessage(message);
    setShowQuickAction(false);
    setQuickActionSearchQuery('');
  };

  const handleSlashTyped = (searchQuery?: string) => {
    if (searchQuery !== undefined) {
      // searchQuery can be empty string (when just "/" is typed) or a search term
      setShowQuickAction(true);
      setQuickActionSearchQuery(searchQuery);
    } else {
      // searchQuery is undefined, meaning message no longer starts with "/"
      setShowQuickAction(false);
      setQuickActionSearchQuery('');
    }
  };

  const handleQuickActionClose = () => {
    setShowQuickAction(false);
    setQuickActionSearchQuery('');
    // Trigger clear of "/" from input
    setClearOnClose(true);
    setTimeout(() => setClearOnClose(false), 100);
  };

  const handleAddBooking = () => {
    setShowAddBooking(true);
  };

  const dropdownMenuItems = [
    {
      id: 'mark-pending',
      label: 'Mark Pending',
      icon: <Clock className="h-4 w-4" />,
      onClick: handleMarkPending,
      disabled: chat.status === 'pending'
    },
    {
      id: 'mark-read',
      label: 'Mark Read',
      icon: <Check className="h-4 w-4" />,
      onClick: handleMarkRead,
      disabled: chat.status === 'read'
    },
    {
      id: 'add-booking',
      label: 'Add Bookings',
      icon: <Calendar className="h-4 w-4" />,
      onClick: handleAddBooking,
      disabled: false
    }
  ];

  const groupConsecutiveMessages = (messages: Message[]) => {
    const grouped: Message[][] = [];
    let currentGroup: Message[] = [];

    messages.forEach((message, index) => {
      const prevMessage = messages[index - 1];
      
      // Don't group special message types (BOT, Note)
      if (message.createdByName === 'BOT' || message.type === 'Note') {
        if (currentGroup.length > 0) {
          grouped.push(currentGroup);
          currentGroup = [];
        }
        grouped.push([message]);
        return;
      }
      
      if (
        prevMessage &&
        prevMessage.byBoz === message.byBoz &&
        prevMessage.createdByName === message.createdByName &&
        prevMessage.createdByName !== 'BOT' &&
        prevMessage.type !== 'Note'
      ) {
        currentGroup.push(message);
      } else {
        if (currentGroup.length > 0) {
          grouped.push(currentGroup);
        }
        currentGroup = [message];
      }
    });

    if (currentGroup.length > 0) {
      grouped.push(currentGroup);
    }

    return grouped;
  };

  if (!user) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">Please log in to view messages</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Chat Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center space-x-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Back to chat list"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          
          {/* Chat Avatar */}
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-primary-600" />
            </div>
          </div>

          {/* Chat Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {getChatTitle()}
              </h2>
              {isMessagePolling && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 font-medium">Live</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {getChatSubtitle()}
            </p>
          </div>
        </div>

        {/* Chat Actions */}
        <div className="flex items-center space-x-2">
          {/* Fast Reply and Quick Actions moved to MessageInput */}
          
          {/* Search Button - Hidden */}
          {/* <button
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Search messages"
          >
            <Search className="h-5 w-5" />
          </button> */}
          
          <DropdownMenu
            items={dropdownMenuItems}
            isOpen={showDropdownMenu}
            onClose={() => setShowDropdownMenu(false)}
            trigger={
              <button
                onClick={() => setShowDropdownMenu(!showDropdownMenu)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Chat menu"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            }
          />
        </div>
      </div>

      {/* Messages Area */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-1"
        onScroll={handleScroll}
      >
        {/* Loading More Messages */}
        {isLoadingMore && (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}

        {/* Loading Initial Messages */}
        {isLoading && chatMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Loading messages...</p>
            </div>
          </div>
        ) : chatMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No messages yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Start the conversation by sending a message
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {groupConsecutiveMessages(chatMessages).map((messageGroup, groupIndex) =>
              messageGroup.map((message, messageIndex) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  currentUser={user}
                  isConsecutive={messageIndex > 0}
                  onReply={handleReplyMessage}
                  onEdit={handleEditMessage}
                  onDelete={handleDeleteMessage}
                  onRetry={handleRetryMessage}
                />
              ))
            )}
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <MessageInput 
        userId={chat.userId} 
        onFastReply={() => setShowFastReply(true)}
        onQuickAction={() => setShowQuickAction(true)}
        onSlashTyped={handleSlashTyped}
        insertMessage={insertMessage}
        onMessageInserted={() => setInsertMessage('')}
        clearOnClose={clearOnClose}
        showQuickActionPopup={showQuickAction}
        quickActionSearchQuery={quickActionSearchQuery}
        onQuickActionSelect={handleQuickActionSelect}
        onQuickActionClose={handleQuickActionClose}
      />

      {/* Fast Reply Modal */}
      <FastReply
        userId={chat.userId}
        isOpen={showFastReply}
        onClose={() => setShowFastReply(false)}
      />

      {/* Quick Action Popup - Only show in modal mode if not using attached mode */}
      {false && (
        <QuickActionPopup
          isOpen={showQuickAction}
          onClose={handleQuickActionClose}
          onSelectMessage={handleQuickActionSelect}
          searchQuery={quickActionSearchQuery}
        />
      )}

      {/* Add Booking Modal */}
      <AddBookingModal
        isOpen={showAddBooking}
        onClose={() => setShowAddBooking(false)}
        chat={chat}
      />
    </div>
  );
};
